import {observable, action, computed, runInAction} from 'mobx';
import encrypt, {rsaEncrypt} from '~/utils/encrypt';
import axiosInstance from '~/utils/axiosInstance';
import Organization from './models/Organization';
import {switchLocale, clearLocaleStorage, querystring} from '~/utils/tools';
import {isInitPassword} from '~/components/ChangeCipher/Init';
import defaultLoginLogo from '~/assets/images/logo_blue.svg';
import {message} from '@enos/dpl';
import {getLocale} from '@locale';
import {
  ACCESS_TOKEN_SESSION_KEY,
  USER_INFO_STORAGE_KEY,
  USER_PERMISSION_TYPE,
  USER_TYPE_SESSION_KEY,
  USER_APP_PERMISSION_SESSION_KEY,
  WORKING_ORGANIZATION_STORAGE_KEY,
  LOGIN_URL_SESSION_KEY,
  VIEW_MODE_STORAGE_KEY,
  DEFAULT_HOMEPAGE,
  STATUS_CODE,
  BROWSER_OPTION
} from '~/utils/constants';
import uris from '~/uris';
import Logo from '~/assets/images/logo_default.svg';

export class SessionStore {
  isSsoLogin = false;

  @observable userPermissionType = USER_PERMISSION_TYPE.ANONYMOUS;

  @observable hasAppPermission = false;

  @observable organizations = [];

  @observable workingOrganizationInfo;

  @observable isLoading = true;

  @observable isDoingLogin = false;

  @observable lionConfiguration = {};

  @observable isMobile = false;

  @observable workingOrganizationLan = [];

  @observable theme = 'DARK';

  @observable isDoingMFA;

  @observable currentUserForMFA;

  @observable showMenu = true;

  @observable initPasswordVisible = false;

  @observable pageLoading = false;

  @observable appSubAuthLevel = true;

  @observable userSubAuthLevel = true;

  @observable roleSubAuthLevel = true;

  @observable userGroupSubAuthLevel = true;

  @observable assetPermissionSubAuthLevel = true;

  @observable auditSubAuthLevel = true;

  @observable currentOperationList = [];

  @observable defaultPath = '';

  @observable showMFAModal = false;

  @observable fromNormal = true;

  @observable profileInfo;

  @observable currentUserInfo = {};

  @observable contentLoading = false;

  tokenInCookie;

  @computed get emailAvaiable() {
    return this.lionConfiguration.emailAvaiable === 'true';
  }

  @computed get roundRobin() {
    return this.lionConfiguration.roundRobin === 'true';
  }

  @computed get ssoConfig() {
    return this.lionConfiguration.ssoConfig === 'true';
  }

  @computed get showCopyright() {
    return this.lionConfiguration.copyrightAvailable === 'true';
  }

  @computed get loginLogo() {
    return this.lionConfiguration.loginLogo;
  }

  // check if need to check device for apps, eg: mobile app will not show in pc web
  @computed get appDeviceIdentify() {
    return this.lionConfiguration.appDeviceIdentify === 'true';
  }

  @computed get languagesConfig() {
    let languages = [];
    try {
      languages = JSON.parse(this.lionConfiguration.languageConfig);
    } catch (error) {
      console.log('wrong language config');
    }
    return languages;
  }

  @computed get currentUser() {
    try {
      return JSON.parse(localStorage.portalGetItem(USER_INFO_STORAGE_KEY));
    } catch {
      return {};
    }
  }

  @computed get accessToken() {
    return this.tokenInCookie;
  }

  @computed get auditEnable() {
    return this.lionConfiguration.auditEnable === 'true';
  }

  @computed get workingOrganization() {
    if (this.workingOrganizationInfo) {
      return this.workingOrganizationInfo;
    }
    const data = JSON.parse(
      localStorage.getItem(WORKING_ORGANIZATION_STORAGE_KEY)
    );
    return data || {};
  }

  @computed get apiAuthorizationList() {
    let apiAuthorizationList = [];
    try {
      apiAuthorizationList = JSON.parse(this.lionConfiguration.apiAuthorizationList);
    } catch (error) {
      console.log(error);
    }
    return apiAuthorizationList;
  }

  @computed get copyrightDisplay() {
    let copyright = {};
    try {
      copyright = JSON.parse(this.lionConfiguration.copyrightDisplay);
    } catch {
      return {};
    }
    return copyright;
  }

  @computed get structureMaxNum() {
    return Number(this.lionConfiguration.structureMaxNum);
  }

  @computed get tokenLocalStorageEnable() {
    return this.lionConfiguration.tokenLocalStorageEnable === 'true';
  }

  @computed get hideToken() {
    return this.lionConfiguration.hideToken === 'true';
  }

  @action.bound
  setContentLoading(data) {
    this.contentLoading = data;
  }

  @action.bound
  getDefaultMenuKey(adminType, currentOperationList) {
    if (adminType === USER_PERMISSION_TYPE.SUB_ADMIN) {
      if (!currentOperationList?.length || currentOperationList?.some(item => item.id === 'user_management')) {
        this.defaultPath = '/admin/users';
      } else {
        if (currentOperationList?.some(item => item.id === 'application_management')) {
          this.defaultPath = '/admin/apps';
          return;
        }
        if (currentOperationList?.some(item => item.id === 'role_management')) {
          this.defaultPath = '/admin/roles';
          return;
        }
        if (currentOperationList?.some(item => item.id === 'user_group_management')) {
          this.defaultPath = '/admin/user-groups';
          return;
        }
        if (currentOperationList?.some(item => item.id === 'asset_permission_assignment')) {
          this.defaultPath = '/admin/asset-permission';
          return;
        }
        if (currentOperationList?.some(item => item.id === 'audit_record')) {
          this.defaultPath = '/admin/audit-log';
        }
      }
    }
  }

  @action.bound
  setDefaultMenuKey(adminType) {
    if (adminType === USER_PERMISSION_TYPE.SUPER_ADMIN) {
      this.defaultPath = '/admin/super';
    }

    if (adminType === USER_PERMISSION_TYPE.OU_ADMIN) {
      this.defaultPath = '/admin/users';
    }
  }

  @action('SSOLogin')
  loginViaCode(body = {}) {
    return axiosInstance
      .post(
        uris.loginViaCode(),
        {...body},
        {
          showError: {
            showPopup: true
          }
        }
      )
      .then((data) => {
        localStorage.setItem(LOGIN_URL_SESSION_KEY, data.ssoLoginUrl);
        data.organizations = [];
        this.handleLoginSuccessful(data);
        if (this.userPermissionType === USER_PERMISSION_TYPE.SUB_ADMIN) {
          this.getCurrentOperationsList();
        } else {
          this.setDefaultMenuKey(this.userPermissionType);
        }
        this.getWorkingOrganization();
      })
      .catch(() => this.clearLoading());
  }

  @action('登录')
  login({account, password}) {
    this.isDoingLogin = true;
    return axiosInstance
      .post(uris.login(), {
        account,
        password: rsaEncrypt(password),
        keyId: encrypt.config.publicKeyId
      }, {
        skipGlobalHandler: true // 跳过全局错误处理
      })
      .then((data) => {
        this.clearLoading();
        if (data.code === STATUS_CODE.PERMISSION_DENIED) {
          message.error(data.message);
          return Promise.reject(data);
        }
        localStorage.setItem(LOGIN_URL_SESSION_KEY, window.location.href);
        localStorage.setItem(VIEW_MODE_STORAGE_KEY, querystring('view-mode') || querystring('viewMode') || querystring('viewmode') || 0);
        this.handleLoginSuccessful(data);
        return {isSuperAdmin: data.isSuperAdmin, mfaType: data.mfaType};
      });
  }

  handleLoginSuccessful(data) {
    this.setCurrentUser(data.user);
    this.setCurrentUserForMFA(data.user);
    sessionStorage.setItem(data.accessToken, data.user?.email);
    this.setOrganizations(data.organizations);
    const permissionType = data.isSuperAdmin
      ? USER_PERMISSION_TYPE.SUPER_ADMIN
      : USER_PERMISSION_TYPE.NORMAL;
    this.setUserPermissionType(permissionType);
    this.setAccessToken(data.accessToken);
    this.setUserTheme(data.user.theme);
    this.setMFAStatus(data.mfaType && data.mfaType !== 'NONE' && data.isSsoLogin !== true);
    this.setSsoLoginStatus(data.isSsoLogin);
    this.clearLoading();
  }

  setCurrentUser(data) {
    localStorage.portalSetItem(USER_INFO_STORAGE_KEY, JSON.stringify(data));
  }

  setAccessToken(token) {
    this.tokenInCookie = token;
    if (this.tokenLocalStorageEnable) {
      localStorage.portalSetItem(ACCESS_TOKEN_SESSION_KEY, token);
    }
  }

  validateCurrentUser({id, name}) {
    if (
      !this.currentUser
      || this.currentUser.id !== id
      || this.currentUser.name !== name
    ) {
      this.logout();
    }
  }

  @action('设置所在企业列表')
  setOrganizations(data) {
    this.organizations = data.map((o) => {
      const org = new Organization(this, o.id);
      org.updateFromJson(o);
      return org;
    });
  }

  @action('获取当前用户所在企业列表')
  getOrganizations() {
    if (this.organizations.length === 0) {
      axiosInstance.post(uris.getUserOrganizationList()).then((data) => {
        this.setOrganizations(data.organizations);
      });
    }
  }

  @action.bound
  getSession() {
    this.isLoading = true;
    return axiosInstance
      .post(uris.getSession(), null, {
        showError: {isShow: false}
      })
      .then((data) => {
        const {adminLevel, hasAppPermission, user, isSsoLogin, id} = data;
        if (data.mfaType === 'EMAIL' && !data?.MFACompleted) {
          this.toggleMFAModal(true);
        }
        this.setAccessToken(id);
        this.setUserPermissionType(adminLevel);
        this.setUserAppPermission(hasAppPermission);
        this.validateCurrentUser(user);
        this.setSsoLoginStatus(isSsoLogin);
        if (adminLevel === USER_PERMISSION_TYPE.SUB_ADMIN) {
          this.getCurrentOperationsList();
        } else {
          this.setDefaultMenuKey(adminLevel);
        }
        this.getWorkingOrganization();
        isInitPassword();
        this.setUserTheme(user.theme);
        this.getProfileInfo();
        this.clearLoading();
      })
      .catch((error) => {
        this.clearLoading();
        return Promise.reject(error);
      });
  }

  @action.bound
  setSession(id) {
    this.pageLoading = true;
    return axiosInstance
      .post(uris.setUserOrganization(), {
        organizationId: id
      }, {
        skipGlobalHandler: true // 跳过全局错误处理
      })
      .then((data) => {
        if (data.code === STATUS_CODE.PERMISSION_DENIED) {
          message.error(data.message);
          return Promise.reject(data);
        }
        const {hasAppPermission, adminLevel, accessToken} = data;
        this.setUserAppPermission(hasAppPermission);
        this.setUserPermissionType(adminLevel);
        this.setAccessToken(accessToken);
        if (adminLevel === USER_PERMISSION_TYPE.SUB_ADMIN) {
          this.getCurrentOperationsList();
        } else {
          this.setDefaultMenuKey(adminLevel);
        }
        this.getWorkingOrganization();
        this.setPageLoading(false);
        this.getProfileInfo();
        return data;
      })
      .catch((error) => {
        if (error.code === STATUS_CODE.UNVERIFY) {
          this.setFromNormal(false);
          this.toggleMFAModal(true);
        }
        this.setPageLoading(false);
      });
  }

  @action.bound
  async logout() {
    if (this.currentUser && !this.isSsoLogin) {
      await axiosInstance.get(uris.logout());
    }

    this.redirectToLogin();
  }

  @action
  async getWorkingOrganization() {
    const data = await axiosInstance.post(uris.getWorkingOrganizationInfo());
    if (data && data.addition) {
      if (/^\//.test(data.addition.logo)) {
        data.addition.logo = `/app-portal${data.addition.logo}`;
      }
      data.logo = data.addition.logo || Logo;
    }
    this.setWorkingOrganization(data);
  }

  @action('clear state')
  clearState() {
    clearLocaleStorage();
    this.setUserPermissionType(USER_PERMISSION_TYPE.ANONYMOUS);
    this.setUserAppPermission(false);
    this.workingOrganizationInfo = null;
  }

  @action('设置用户权限登记')
  setUserPermissionType(type) {
    this.userPermissionType = type;
    localStorage.setItem(USER_TYPE_SESSION_KEY, type);
  }

  @action('设置是否有app权限')
  setUserAppPermission(boolean) {
    this.hasAppPermission = boolean;
    localStorage.setItem(USER_APP_PERMISSION_SESSION_KEY, boolean);
  }

  @action('设置工作企业')
  setWorkingOrganization(data) {
    localStorage.setItem(
      WORKING_ORGANIZATION_STORAGE_KEY,
      JSON.stringify(data)
    );
    this.workingOrganizationInfo = data;
    this.setLanguage(data.addition.languages);
  }

  @action.bound
  getCurrentOperationsList() {
    this.setContentLoading(true);
    axiosInstance
      .post(uris.getCurrentOperationsList())
      .then((data) => {
        this.setCurrentOperationList(data);
        this.setContentLoading(false);
      });
  }

  @action.bound
  setCurrentOperationList(data) {
    this.getDefaultMenuKey(this.userPermissionType, data);
    this.currentOperationList = data;
    if (!data?.some(item => item.id === 'application_management')) {
      this.appSubAuthLevel = false;
    }
    if (!data?.some(item => item.id === 'user_management')) {
      this.userSubAuthLevel = false;
    }
    if (!data?.some(item => item.id === 'role_management')) {
      this.roleSubAuthLevel = false;
    }
    if (!data?.some(item => item.id === 'user_group_management')) {
      this.userGroupSubAuthLevel = false;
    }
    if (!data?.some(item => item.id === 'asset_permission_assignment')) {
      this.assetPermissionSubAuthLevel = false;
    }
    if (!data?.some(item => item.id === 'audit_record')) {
      this.auditSubAuthLevel = false;
    }
  }

  @action('clear loading')
  clearLoading() {
    this.isLoading = false;
    this.isDoingLogin = false;
  }

  @action('设置用户权限登记')
  getLionConfiguration() {
    return axiosInstance
      .post(uris.getLionConfiguration(), {
        keys: [
          'app-portal.admin.help.url',
          'app-portal.email.avaiable',
          'app-portal.copyright.available',
          'app-portal.login.logo',
          'app-portal.feature.app.device.identify',
          'app-portal.message.frequency',
          'app-portal.alarm.round.robin',
          'app-portal.sso.config.enable',
          'app-portal.menu.audit.enable',
          'app-portal.default.homepage.enable',
          'app-portal.language.config',
          'app-portal.project.browser.option',
          'app-portal.rate.login.principal.duration',
          'app-portal.app.authorization.list',
          'app-portal.copyright.display',
          'app-portal.app.structure.max',
          'app-portal.token.localStorage.enable',
          'app-portal.app.url.token.hide.enable'
        ]
      })
      .then((data) => {
        runInAction(() => {
          this.lionConfiguration = {
            adminHelpUrl: data['app-portal.admin.help.url'],
            emailAvaiable: data['app-portal.email.avaiable'],
            copyrightAvailable: data['app-portal.copyright.available'],
            loginLogo: data['app-portal.login.logo'] || defaultLoginLogo,
            appDeviceIdentify: data['app-portal.feature.app.device.identify'],
            messageFrequency: data['app-portal.message.frequency'],
            roundRobin: data['app-portal.alarm.round.robin'],
            ssoConfig: data['app-portal.sso.config.enable'],
            auditEnable: data['app-portal.menu.audit.enable'],
            defaultHomepage: data['app-portal.default.homepage.enable'],
            languageConfig: data['app-portal.language.config'],
            browser: data['app-portal.project.browser.option'],
            loginPrincipalDuration: data['app-portal.rate.login.principal.duration'],
            apiAuthorizationList: data['app-portal.app.authorization.list'],
            copyrightDisplay: data['app-portal.copyright.display'],
            structureMaxNum: data['app-portal.app.structure.max'],
            tokenLocalStorageEnable: data['app-portal.token.localStorage.enable'],
            hideToken: data['app-portal.app.url.token.hide.enable']
          };
          localStorage.setItem(DEFAULT_HOMEPAGE, data['app-portal.default.homepage.enable']);
          localStorage.setItem(BROWSER_OPTION, data['app-portal.project.browser.option']);
        });
      });
  }

  @action('set view state')
  setDeviceInfo(boolean) {
    if (this.isMobile !== boolean) {
      this.isMobile = boolean;
    }
  }

  redirectToLogin(redirect, isExpired = false) {
    const token = this.accessToken;

    this.clearState();

    let url = localStorage.getItem(LOGIN_URL_SESSION_KEY);

    // only if this is domain user && not expired && login by site/{idp}
    if (this.isSsoLogin && !isExpired && (!url || decodeURIComponent(url).includes('/site/'))) {
      window.location.href = uris.ssoLogin(token);
      return false;
    }

    if (url) {
      url = decodeURIComponent(url);
    } else {
      url = `${window.location.origin}/login`;
    }

    const computedUrl = new URL(url);
    computedUrl.searchParams.delete('redirect');
    if (redirect) {
      computedUrl.searchParams.append('redirect', redirect);
    }

    window.location.href = computedUrl.href;

    return true;
  }

  redirectToSSO(redirect) {
    window.location.href = new URL(redirect).href;
  }

  // renewal token
  tokenRenewal() {
    axiosInstance
      .post(uris.getSession(), null, {
        showError: {isShow: false}
      });
  }

  @action.bound
  setLanguage(arr) {
    this.workingOrganizationLan = arr;
    if ((this.workingOrganizationLan && this.workingOrganizationLan.length === 1) || !this.workingOrganizationLan.includes(getLocale())) {
      switchLocale(this.workingOrganizationLan[0]);
    }
  }

  @action.bound
  setUserTheme(theme) {
    this.theme = theme;
  }

  @action.bound
  sendCode() {
    return axiosInstance.post(uris.sendVeritficationCode());
  }

  @action.bound
  verifyVerificationCode(verifyCode) {
    return axiosInstance.post(
      uris.verifyVerificationCode(),
      {
        verify_code: verifyCode
      },
      {
        showError: {
          isShow: false
        }
      }
    );
  }

  @action.bound
  setMFAStatus(boo) {
    this.isDoingMFA = boo;
  }

  @action.bound
  setCurrentUserForMFA(user) {
    this.currentUserForMFA = user;
  }

  @action.bound
  toggleMenu(boolean = !this.showMenu) {
    this.showMenu = boolean;
  }

  setSsoLoginStatus(boo) {
    this.isSsoLogin = boo;
  }

  @action.bound
  toggleInitPassword(boo) {
    this.initPasswordVisible = boo;
  }

  @action.bound
  setPageLoading(boo) {
    this.pageLoading = boo;
  }

  @action.bound
  toggleMFAModal(val) {
    this.showMFAModal = val;
  }

  @action.bound
  setFromNormal(boolean) {
    this.fromNormal = boolean;
  }

  @action.bound
  setProfileInfo(data) {
    this.profileInfo = data;
  }

  @action.bound
  setCurrentUserInfo(data) {
    this.currentUserInfo = data;
  }

  @action.bound
  getProfileInfo() {
    axiosInstance
      .get(uris.getProfileInfo())
      .then((data) => {
        this.setProfileInfo(data?.fields);
        this.setCurrentUserInfo(data?.info);
      });
  }
}

export default new SessionStore();
